import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Carrot,
  Save,
  ArrowUpDown
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UNITS = ['kg', 'g', 'L', 'ml', 'bucket', 'spoon', 'pcs', 'cup', 'tbsp', 'tsp'];

export const Ingredients = () => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    default_unit: 'g',
    master_sort_order: 9999
  });

  useEffect(() => {
    fetchIngredients();
  }, []);

  const fetchIngredients = async () => {
    try {
      const response = await axios.get(`${API}/ingredients`);
      setIngredients(response.data);
    } catch (error) {
      toast.error('Failed to fetch ingredients');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingIngredient(null);
    setFormData({
      name: '',
      default_unit: 'g',
      master_sort_order: ingredients.length + 1
    });
    setShowDialog(true);
  };

  const openEditDialog = (ingredient) => {
    setEditingIngredient(ingredient);
    setFormData({
      name: ingredient.name,
      default_unit: ingredient.default_unit,
      master_sort_order: ingredient.master_sort_order
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Ingredient name is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        master_sort_order: parseInt(formData.master_sort_order) || 9999
      };

      if (editingIngredient) {
        await axios.put(`${API}/ingredients/${editingIngredient.id}`, payload);
        toast.success('Ingredient updated');
      } else {
        await axios.post(`${API}/ingredients`, payload);
        toast.success('Ingredient created');
      }
      setShowDialog(false);
      fetchIngredients();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save ingredient');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ingredient) => {
    if (!window.confirm(`Delete "${ingredient.name}"?`)) return;

    try {
      await axios.delete(`${API}/ingredients/${ingredient.id}`);
      toast.success('Ingredient deleted');
      fetchIngredients();
    } catch (error) {
      toast.error('Failed to delete ingredient');
    }
  };

  const filteredIngredients = ingredients.filter(ing =>
    ing.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ingredients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight" >
            Ingredients
          </h1>
          <p className="text-stone-500 mt-1">{ingredients.length} ingredients in master list</p>
        </div>
        <button
          onClick={openCreateDialog}
          className="btn-primary"
          data-testid="add-ingredient-btn"
        >
          <Plus size={20} />
          <span>Add Ingredient</span>
        </button>
      </div>

      {/* Search */}
      <div className="virsa-card p-4">
        <div className="relative">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none z-10" />
          <input
            type="text"
            placeholder="Search ingredients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="virsa-input !pl-11"
            data-testid="ingredient-search"
          />
        </div>
      </div>

      {/* Table */}
      {filteredIngredients.length > 0 ? (
        <div className="virsa-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="virsa-table">
              <thead>
                <tr>
                  <th className="w-16">
                    <div className="flex items-center gap-1">
                      <ArrowUpDown size={14} />
                      Order
                    </div>
                  </th>
                  <th>Name</th>
                  <th>Default Unit</th>
                  <th className="w-32 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredIngredients.map((ingredient) => (
                  <tr key={ingredient.id} data-testid={`ingredient-row-${ingredient.id}`}>
                    <td>
                      <span className="badge badge-teal">{ingredient.master_sort_order}</span>
                    </td>
                    <td className="font-medium">{ingredient.name}</td>
                    <td>{ingredient.default_unit}</td>
                    <td>
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditDialog(ingredient)}
                          className="p-2 text-[#124242] hover:bg-[#124242]/10 rounded-lg transition-colors"
                          data-testid={`edit-ingredient-${ingredient.id}`}
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(ingredient)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          data-testid={`delete-ingredient-${ingredient.id}`}
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="virsa-card p-12 text-center">
          <Carrot size={48} className="mx-auto text-stone-300 mb-4" />
          <h3 className="text-xl font-semibold text-stone-600 mb-2">No ingredients found</h3>
          <p className="text-stone-500 mb-4">
            {searchQuery ? 'Try a different search term' : 'Start by adding your first ingredient'}
          </p>
          <button onClick={openCreateDialog} className="btn-primary">
            <Plus size={20} />
            Add Ingredient
          </button>
        </div>
      )}

      {/* Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl" >
              {editingIngredient ? 'Edit Ingredient' : 'Add Ingredient'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="virsa-input"
                placeholder="e.g., Onion"
                data-testid="ingredient-name-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Default Unit *</label>
              <select
                value={formData.default_unit}
                onChange={(e) => setFormData({ ...formData, default_unit: e.target.value })}
                className="virsa-input"
                data-testid="ingredient-unit-select"
              >
                {UNITS.map(unit => (
                  <option key={unit} value={unit}>{unit}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-600 mb-2">Sort Order</label>
              <input
                type="number"
                min="1"
                value={formData.master_sort_order}
                onChange={(e) => setFormData({ ...formData, master_sort_order: e.target.value })}
                className="virsa-input"
                placeholder="1"
                data-testid="ingredient-order-input"
              />
              <p className="text-xs text-stone-500 mt-1">
                Lower numbers appear first in production lists
              </p>
            </div>
          </div>

          <DialogFooter>
            <button onClick={() => setShowDialog(false)} className="btn-outline">
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
              data-testid="save-ingredient-btn"
            >
              {saving ? <span className="spinner" /> : <Save size={18} />}
              <span>{editingIngredient ? 'Update' : 'Create'}</span>
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
